import { Mesh, Object3D } from 'three';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { CloneUtils } from '@shared/util/clone-utils';
import { ConnectionAreaVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleInfoVO } from '@project/model/vo/project.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

export class BaseDynamicArticlePostParseProcessor {

  public canModify3DArticleGeometry(): boolean {
    return true;
  }

  public process(container: ArticleContainer, article: ArticleVO, renderOrder: number = 0): void {
    if (this.canModify3DArticleGeometry() && container.articleObject3DDataShared) {
      throw new Error('Modifying a shared article geometry is not allowed!');
    }
    const clonedArticle: ArticleVO = CloneUtils.cloneArticleVO(article);

    const originalDynamicConnectionNames: string[] = [];
    let originalConnectionNames: string[] = [];
    const staticMetaElements: MetaElementVO[] = this.getStaticMetaElements(article);
    for (const staticMetaElement of staticMetaElements) {
      originalConnectionNames.push(staticMetaElement.name);
    }
    const articleInfo: ArticleInfoVO = container.projectModelService.getProcessedArticleInfo(container.articleGUID);
    if (!articleInfo) {
      for (const con of article.metaElements) {
        if (!ConnectionInfoUtils.isStaticMetaElement(con)) {
          originalDynamicConnectionNames.push(con.name);
        }
      }
      // remove old meta elements
      clonedArticle.metaElements = this.createDynamicMetaElements(container, article.metaElements);
    } else {
      for (const metaInfo of articleInfo.metaElements) {
        if (originalDynamicConnectionNames.indexOf(metaInfo.originalName) === -1) {
          originalDynamicConnectionNames.push(metaInfo.originalName);
        }
      }
      clonedArticle.metaElements = this.getDynamicMetaElementsByInfo(clonedArticle, articleInfo);
    }
    clonedArticle.metaElements = staticMetaElements.concat(clonedArticle.metaElements);
    originalConnectionNames = originalConnectionNames.concat(originalDynamicConnectionNames);

    this.processOriginalDynamicConnections(container, originalDynamicConnectionNames);

    if (container.projectModel && container.properties.containsKey(ArticleContainerPropertyDef.STRETCH_LENGTH)) {
      const stretchLength: number = this.getStretchLength(container);
      this.changeMeshes(container, container.model, originalConnectionNames, originalDynamicConnectionNames, stretchLength, renderOrder);
    }

    container.updateArticle(clonedArticle);
  }

  protected getStretchLength(container: ArticleContainer): number {
    return ArticleContainerUtils.getDynamicArticleStretchLength(container);
  }

  protected processOriginalDynamicConnections(container: ArticleContainer, originalConnectionNames: string[]): void {}

  private changeMeshes(
    articleContainer: ArticleContainer,
    model: Object3D,
    connectionNames: string[],
    dynamicConnectionNames: string[],
    extrusionLength: number,
    renderOrder: number
  ): void {
    const children: Object3D[] = Object3DUtils.getChildrenRecursively(model);
    for (const child of children) {
      this.changeMeshes(articleContainer, child, connectionNames, dynamicConnectionNames, extrusionLength, renderOrder);
      if (IsThreeJsType.mesh(child)) {
        if (connectionNames.indexOf(child.name) === -1) {
          // if mesh of dynamic article
          const extrusion: Mesh = ArticlesCreator.createDynamicArticle(child, extrusionLength);
          if (extrusion) {
            model.remove(child);
            Object3DUtils.dispose(child);
            model.add(extrusion);
            extrusion.renderOrder = renderOrder;
          }
        } else {
          this.changeConnectionMesh(child, model, articleContainer, dynamicConnectionNames, extrusionLength);
        }
      }
    }
  }

  protected changeConnectionMesh(
    connectionMesh: Mesh,
    model: Object3D,
    articleContainer: ArticleContainer,
    dynamicConnectionNames: string[],
    extrusionLength: number
  ): void {
    if (dynamicConnectionNames.indexOf(connectionMesh.name) === -1) {
      this.changeStaticConnectionMesh(connectionMesh, model, articleContainer, extrusionLength);
    }
  }

  protected changeStaticConnectionMesh(
    connectionMesh: Mesh,
    parentContainer: Object3D,
    articleContainer: ArticleContainer,
    extrusionLength: number
  ): void {}

  protected removeFromParent(childContainer: Object3D, parentContainer: Object3D): void {
    parentContainer.remove(childContainer);
    Object3DUtils.destroy(childContainer);
  }

  private getStaticMetaElements(article: ArticleVO): MetaElementVO[] {
    if (!article || !article.metaElements) {
      return [];
    }
    const staticMetaElements: MetaElementVO[] = [];
    for (const metaElement of article.metaElements) {
      if (ConnectionInfoUtils.isStaticMetaElement(metaElement)) {
        staticMetaElements.push(metaElement);
      }
    }
    return staticMetaElements;
  }

  private createDynamicMetaElements(articleContainer: ArticleContainer, originalMetaElements: MetaElementVO[]): MetaElementVO[] {
    if (!articleContainer.properties.containsKey(ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS)) {
      return [];
    }

    const metaElements: MetaElementVO[] = [];
    const originalGivingConnectionArea: ConnectionAreaVO = ArticleUtils.findFirstConnectionArea(originalMetaElements, ConnectionAreaVO.SIDE_GIVING);
    const originalTextureArea: TextureAreaVO = ArticleUtils.findFirstTextureArea(originalMetaElements);

    const acceptingConnections: Connection[] = articleContainer.properties.getValue(
      ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS
    ) as Connection[];
    let connectionPosition: number = 1;
    let texturePosition: number = 1;
    // create connection area
    const acceptingConnectionsQuantity = acceptingConnections.length;
    for (let i: number = 0; i < acceptingConnectionsQuantity; i++) {
      while (
        articleContainer.findFirstConnectionByName(MetaElementVO.CONNECTION_PREFIX + connectionPosition) ||
        articleContainer.findFirstConnectionByName(MetaElementVO.TEXTURE_PREFIX + connectionPosition)
      ) {
        connectionPosition++;
        texturePosition++;
      }

      const connectionArea: MetaElementVO = ConnectionInfoUtils.cloneMetaElement(
        MetaElementVO.CONNECTION_PREFIX + connectionPosition,
        originalGivingConnectionArea
      );
      metaElements.push(connectionArea);

      connectionPosition++;
    }

    if (originalTextureArea) {
      let textureConnectionsCount: number = this.getPredefinedTextureConnectionsCount(articleContainer);
      if (!NumberUtils.isNumber(textureConnectionsCount)) {
        textureConnectionsCount = connectionPosition - texturePosition;
      }
      this.createTextureElements(texturePosition, textureConnectionsCount, originalTextureArea, metaElements);
    }

    return metaElements;
  }

  protected getPredefinedTextureConnectionsCount(articleContainer: ArticleContainer): number {
    return NaN;
  }

  private createTextureElements(
    texturePosition: number,
    textureConnectionsCount: number,
    originalTextureArea: TextureAreaVO,
    createdMetaElements: MetaElementVO[]
  ): void {
    let startTexturePosition: number = texturePosition;
    for (let i: number = 0; i < textureConnectionsCount; i++) {
      const textureArea: MetaElementVO = ConnectionInfoUtils.cloneMetaElement(
        MetaElementVO.TEXTURE_PREFIX + startTexturePosition,
        originalTextureArea
      );
      createdMetaElements.push(textureArea);

      startTexturePosition++;
    }
  }

  private getDynamicMetaElementsByInfo(article: ArticleVO, articleInfo: ArticleInfoVO): MetaElementVO[] {
    const articleMetaElements: MetaElementVO[] = [];
    for (const metaInfo of articleInfo.metaElements) {
      let metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, metaInfo.name);
      if (!metaElement) {
        const originalMetaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, metaInfo.originalName);
        if (originalMetaElement) {
          metaElement = ConnectionInfoUtils.cloneMetaElement(metaInfo.name, originalMetaElement);
        }
      }
      if (metaElement) {
        articleMetaElements.push(metaElement);
      }
    }
    return articleMetaElements;
  }
}
